import type { ReadonlyRequestCookies } from "next/dist/server/web/spec-extension/adapters/request-cookies";
import type { NextFetchEvent, NextMiddleware, NextRequest, NextResponse } from "next/server";
import "reflect-metadata";

import type { SelfSignUpConsent, SelfSignUpResult, Session } from "../domain";

export type AppRouteHandlers = Record<"GET" | "POST", (req: NextRequest) => Promise<Response>>;

export type AppSsoRouteHandlers = Record<"GET", (req: NextRequest) => Promise<Response>>;

export type ChainableMiddleware = (request: NextRequest, event: NextFetchEvent) => Promise<NextResponse>;

export type MiddlewareFactory = (middleware: ChainableMiddleware) => ChainableMiddleware;

export type AuthMiddlewareConfiguration = {
  /**
   * A route or a URL of sign in page (URL use in case multiple subdomain signIn)
   * For example: '/login' or 'https://authentication.pharaday.net/login'
   */
  signInUrl: string;
  /**
   * A route or a URL that the authenticated user will be redirect if they access authentication routes
   * default: '/'
   */
  signInCallbackUrl?: string;
  /**
   * A list of routes that should be accessible with/without authentication
   * For example: ['/foo', '/bar(.*)'] or [/^\/foo\/.*$/]
   */
  publicRoutes?: string[];
  /**
   * A list of routes that should only be accessible without authentication
   * For example: ['/foo', '/bar(.*)'] or [/^\/foo\/.*$/]
   */
  guestRoutes?: string[];
  /**
   * This middleware run after the authorization finished.
   * Meaning, it will run
   * - After public route flow
   * - After the authorization flow valid
   *
   * @important This will NOT run if the authorization flow failed
   * Meaning the user will be redirect to signInCallbackUrl.
   */
  afterAuth?: ChainableMiddleware;
};

export type SsoRouteHandlerConfiguration = {
  /**
   * A route or a URL of sign in page (URL use in case multiple subdomain signIn)
   * For example: '/login' or 'https://authentication.pharaday.net/login'
   */
  signInUrl: string;
  /**
   * A route or a URL that the authenticated user will be redirect if they access authentication routes
   * default: '/'
   */
  signInCallbackUrl?: string;
};

export type Credentials =
  | {
      type: "with-password";
      email: string;
      password: string;
    }
  | {
      type: "with-magic-link";
      email: string;
      code: string;
    }
  | {
      type: "with-totp";
      pendingToken: string;
      challenge: string;
      code: string;
    }
  | {
      type: "with-email-validation";
      pendingToken: string;
      code: string;
    }
  | {
      type: "with-sso";
      code: string;
    }
  | ({
      type: "with-self-signup";
      token: string;
      code: string;
      name: string;
    } & SelfSignUpConsent);

export abstract class AuthProvider {
  abstract getRouteHandlers(): AppRouteHandlers;
  abstract getSsoRouteHandlers(configuration: SsoRouteHandlerConfiguration): AppSsoRouteHandlers;
  abstract authMiddleware(configuration: AuthMiddlewareConfiguration): NextMiddleware;
  abstract getSignInCallbackUrl(cookie: ReadonlyRequestCookies): string | undefined;
  abstract signIn(
    credentials: Credentials
  ): Promise<{ success: boolean; pendingToken?: string; response?: NextResponse } & SelfSignUpResult>;
  abstract signOut(): Promise<{ success: boolean }>;
  abstract getSession(): Promise<Session | null>;
  abstract updateSession(session: Session): Promise<void>;
  abstract renewSession(): Promise<Session | null>;
}
